// imports
import Link from 'next/link';
import { useAtom } from 'jotai';
import { useState, Ref } from 'react';

// lib files
import { navigationAtom, NavigationStateEnum } from '@/lib/atoms/navigation';
import { DONATE_FALLBACK_URL, PBS_SHOP_URL } from '@/lib/constants';
import { ProfileData } from '@/lib/types/api/profile-data';
import { sendPbsGtmEvent } from '@/lib/helpers/send-pbs-gtm-event';
import { StationData } from "@/lib/types/api/stations-data";
import { useCurrentUrl, useHasMounted } from '@/lib/hooks';
import { userProfile } from '@/lib/atoms/profile';

// components
import MobileMenuButton from '@/components/Navigation/MobileMenuButton';
import MobileMenuGenres from '@/components/Navigation/MobileMenuGenres';
import MobileMenuUser from '@/components/Navigation/MobileMenuUser';
import NavLink  from '@/components/Navigation/NavLink';
import SignInButton from '@/components/Navigation/SignInButton';

// svgs
import DonateIcon from '@/public/svg/donate-heart.svg';
import LiveTVEllipse from '@/public/svg/ellipse.svg';
import MyListIcon from '@/public/svg/my-list-check-white.svg';
import MyStationIcon from '@/public/svg/my-station-white.svg';
import ShopIcon from '@/public/svg/pbs-shop.svg';
import ShowsIcon from '@/public/svg/shows-white.svg';

// styles
import styles from './MobileMenu.module.scss';

interface MobileMenuProps {
  depIsOpen?: boolean;
  isAccessibilityExploreHubEnabled: boolean;
  isMysteryExploreHubEnabled: boolean;
  isSVP: boolean;
  isUS: boolean;
  ketchEnabled?: boolean;
  profileStub?: ProfileData | null;
  ref?: Ref<HTMLDivElement>;
  stationData?: StationData;
}

function MobileMenu({
  depIsOpen,
  isAccessibilityExploreHubEnabled,
  isMysteryExploreHubEnabled,
  isSVP,
  isUS,
  ketchEnabled,
  profileStub,
  ref,
  stationData,
}: MobileMenuProps) {
  const [ navigation, setNavigation ] = useAtom(navigationAtom);
  const [ profile, setProfile] = useAtom(userProfile);
  const [ genresOpen, setGenresOpen ] = useState(false);
  const [ userSettingsOpen, setUserSettingsOpen ] = useState(false);
  const isOpen = depIsOpen || navigation === NavigationStateEnum.MobileMenuOpen;
	const donate_url = stationData?.attributes.donate_url;
  const isLocalized = !!(stationData?.id);
  const isStationPassportEnabled = !!(stationData?.attributes.passport_enabled);

  const submenuClickHandler = () => {
    setNavigation(NavigationStateEnum.Default)
  }

  const hasMounted = useHasMounted();

  const currentUrl = useCurrentUrl() || '';
  const baseDonateUrl = donate_url || DONATE_FALLBACK_URL;

  const donateUrl = (() => {
    try {
      const url = new URL(baseDonateUrl);
      if (currentUrl) {
        url.searchParams.set('referrer', currentUrl);
      }
      return url.toString();
    } catch {
      return baseDonateUrl;
    }
  })();

  // this weird block of code is really here for the storybook
  // we only pass `null` if we want to show the logged out state
  if (profileStub === null) {
    setProfile(null);
  } else if (profileStub) {
    setProfile(profileStub);
  }

  let classNames = `${styles.mobile_menu}`

  if (isOpen) {
    classNames += ` ${styles.is_open}`
  }

  if (genresOpen) {
    classNames += ` ${styles.genres_submenu_open}`
  } else if (userSettingsOpen) {
    classNames += ` ${styles.user_settings_submenu_open}`
  }

  return (
    <div className={styles.mobile_menu_wrapper} ref={ref}>
      <MobileMenuButton />
      <nav className={classNames}>
        <div className={styles.mobile_menu_inner}>
          <MobileMenuGenres
            genresOpen={genresOpen}
            isAccessibilityExploreHubEnabled={isAccessibilityExploreHubEnabled}
            isMysteryExploreHubEnabled={isMysteryExploreHubEnabled}
            setGenresOpen={setGenresOpen}
            onClick={submenuClickHandler}
            isLocalized={isLocalized}
            isPassportEnabled={isStationPassportEnabled}
          />

          <ul className={styles.links}>
            { isUS && isLocalized && (
              <li>
                <NavLink href="/livestream/">
                  <LiveTVEllipse className={styles.live_tv_icon} />
                  Live TV
                </NavLink>
              </li>
            )}
            <li>
              <NavLink href="/shows/">
                <ShowsIcon className={styles.shows_icon} />
                Shows
              </NavLink>
            </li>
            { !isSVP && isLocalized && (
            <li>
              <NavLink href="/my-station/">
                <MyStationIcon className={styles.my_station_icon} />
                My Station
              </NavLink>
            </li>
            )}
            <li>
              <NavLink href="/my-list/shows/">
                <MyListIcon className={styles.my_list_icon} />
                My List
              </NavLink>
            </li>
            <li>
              <Link
                href={PBS_SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                prefetch={false}
                onClick={() => {
                  sendPbsGtmEvent({
                    feature_category: "mobile nav shop link",
                    feature_name: "mobile nav shop link",
                    object_name: "mobile nav shop link",
                    object_text: "Shop",
                    object_type: "link",
                    object_action: "click",
                    object_action_behavior: "send user to pbs shop url",
                    object_url: PBS_SHOP_URL,
                })}}
              >
                <ShopIcon className={styles.shop_icon} />
                Shop
              </Link>
            </li>
            {	donateUrl && (
            <li className={styles.push}>
              <Link
                href={donateUrl}
                className={styles.donate_link}
                prefetch={false}
              >
                <DonateIcon className={styles.donate_icon} />
                Donate
              </Link>
            </li>
            )}

            <li className={styles.personal}>
              { profile && hasMounted ? (
                  <MobileMenuUser
                    userSettingsOpen={userSettingsOpen}
                    setUserSettingsOpen={setUserSettingsOpen}
                    onClick={submenuClickHandler}
                    profile={profile}
                    isLocalized={isLocalized}
                    isStationPassportEnabled={isStationPassportEnabled}
                    ketchEnabled={ketchEnabled}
                  />
                ) : (
                  <SignInButton className={styles.sign_in} size="max" />
                )
              }
            </li>
          </ul>
        </div>
      </nav>
    </div>
  );
}

export default MobileMenu;
